import type { ApiError } from '../types';

// ─── 설정 ────────────────────────────────────────────────────────────────────

// 같은 도메인(otj-shop.suparain.kr)에서 API와 대시보드를 서빙하므로 상대경로 사용
const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

// ─── 토큰 관리 (HttpOnly 쿠키 기반) ──────────────────────────────────────────
//
// 카페24 OAuth 로그인 후 서버에서 otj_token HttpOnly 쿠키를 발급합니다.
// JS에서 직접 읽을 수 없고, 브라우저가 same-origin 요청 시 자동으로 전송합니다.
// credentials: 'same-origin' 설정으로 쿠키가 자동 포함됩니다.
//
// tokenStorage는 하위 호환성을 위해 유지하되 실제 저장은 하지 않습니다.
// (이메일/비밀번호 관리자 로그인 경로가 향후 추가될 경우를 위해 구조 보존)

export const tokenStorage = {
  getAccessToken: (): string | null => null,
  getRefreshToken: (): string | null => null,
  setTokens: (_accessToken: string, _refreshToken: string): void => { /* no-op: 쿠키 기반 인증 */ },
  clearTokens: (): void => { /* no-op: 서버 /auth/logout에서 쿠키 만료 처리 */ },
};

// ─── 에러 클래스 ─────────────────────────────────────────────────────────────

export class ApiClientError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// ─── 핵심 fetch 래퍼 ─────────────────────────────────────────────────────────
//
// 쿠키 기반 인증으로 전환되어 토큰 갱신 로직이 단순화되었습니다.
// credentials: 'same-origin'으로 otj_token 쿠키가 자동 포함됩니다.
// 401 응답 시 카페24 앱스토어 재설치 안내 페이지로 이동합니다.

async function request<T>(
  method: string,
  path: string | null,
  body?: unknown,
): Promise<T> {
  if (path === null) {
    throw new ApiClientError(400, 'INVALID_PATH', '잘못된 API 경로입니다.');
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin', // 같은 도메인 쿠키 자동 전송 (otj_token)
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // 401 - 세션 만료 → 로그인 페이지로 이동 (카페24 앱스토어 재설치 안내)
  if (response.status === 401) {
    window.location.href = '/login';
    throw new ApiClientError(401, 'UNAUTHORIZED', '로그인이 필요합니다.');
  }

  return parseResponse<T>(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let json: unknown;

  try {
    json = JSON.parse(text);
  } catch {
    if (!response.ok) {
      throw new ApiClientError(response.status, 'PARSE_ERROR', `서버 응답 오류 (${response.status})`);
    }
    return text as unknown as T;
  }

  if (!response.ok) {
    const error = json as { code?: string; message?: string; details?: unknown };
    throw new ApiClientError(
      response.status,
      error.code ?? 'UNKNOWN_ERROR',
      error.message ?? '알 수 없는 오류가 발생했습니다.',
      error.details
    );
  }

  const typed = json as { success?: boolean; data?: T } & T;
  if ('success' in typed && 'data' in typed && typed.data !== undefined) {
    return typed.data;
  }
  return json as T;
}

// ─── HTTP 메서드 ─────────────────────────────────────────────────────────────

export const apiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

export type { ApiError };
